import { dayjs, formatDate } from '@koob/dates';
import { getSetting, getSettingLabel, isTransferTypology } from '@koob/utils';
import ExportFlowPage from './ExportFlowPage';
import { sanitizeDiaryHtml } from '../richText';

export function getProgramGroups(trip, experiences) {
  const groups = [];

  (trip?.tripDays ?? []).forEach((day, index) => {
    const experienceId = day?.experienceId;
    if (!experienceId) return;

    const previous = groups[groups.length - 1];
    if (previous?.experienceId === experienceId) {
      previous.dayIndices.push(index);
      return;
    }

    groups.push({ experienceId, dayIndices: [index] });
  });

  return groups.map((group) => ({
    ...group,
    experience: experiences?.find(({ id }) => id === group.experienceId),
  }));
}

function dayDate(trip, index) {
  return dayjs(trip?.startDate).add(index, 'days');
}

function formatRange(from, to) {
  const start = formatDate(from, 'DD MMM YYYY');
  const end = formatDate(to, 'DD MMM YYYY');
  if (!start) return null;
  return start === end ? start : `${start} — ${end}`;
}

function formatSpan(from, to) {
  const start = formatDate(from, 'DD MMM YYYY');
  const end = formatDate(to, 'DD MMM YYYY');
  if (!start) return null;
  return start === end ? start : `From ${start} to ${end}`;
}

// The last trip day of the experience a day belongs to: its consecutive days,
// read the way `getProgramGroups` groups them.
function lastDayOfExperience(trip, dayIndex) {
  const days = trip?.tripDays ?? [];
  const experienceId = days[dayIndex]?.experienceId;
  let last = dayIndex;
  while (experienceId && days[last + 1]?.experienceId === experienceId) {
    last += 1;
  }
  return last;
}

/**
 * The date shown for a whole-experience service. A transfer runs on the last
 * day of its experience — one date, as in the Trip Designer; any other service
 * keeps the trip span. The saved option names the (1-based) day it is filed on,
 * the first of its experience; its fetched detail tells a transfer apart.
 */
export function globalServiceDateLabel(trip, option, extraExperiences) {
  const extra = extraExperiences?.[option?.optionId ?? option?.id] ?? option;
  if (option?.dayIndex == null || !isTransferTypology(extra, [])) {
    return formatSpan(
      dayDate(trip, 0),
      dayDate(trip, (trip?.tripDays?.length ?? 1) - 1),
    );
  }
  return formatDate(
    dayDate(trip, lastDayOfExperience(trip, option.dayIndex - 1)),
    'DD MMM YYYY',
  );
}

function collectServices(trip, dayIndices) {
  const byName = new Map();

  dayIndices.forEach((index) => {
    (trip?.tripDays?.[index]?.options ?? []).forEach((option) => {
      const key = option?.name ?? option?.optionId ?? option?.id;

      if (byName.has(key)) {
        byName.get(key).indices.push(index);
        return;
      }
      byName.set(key, { option, indices: [index] });
    });
  });

  return [...byName.values()].map(({ option, indices }) => {
    const sorted = [...indices].sort((a, b) => a - b);
    const contiguous = sorted.every(
      (value, position) => position === 0 || value === sorted[position - 1] + 1,
    );

    return {
      option,
      dateLabel: contiguous
        ? formatSpan(
            dayDate(trip, sorted[0]),
            dayDate(trip, sorted[sorted.length - 1]),
          )
        : sorted
            .map((index) => formatDate(dayDate(trip, index), 'DD MMM YYYY'))
            .join(' · '),
    };
  });
}

function ServiceList({ title, services, extraExperiences, includedLabel }) {
  if (services.length === 0) return null;

  return (
    <div className="mt-5">
      <div className="mb-2 text-2xs font-bold uppercase tracking-widest text-[var(--td-ink-muted)]">
        {title}
      </div>

      <div className="grid grid-cols-2 rounded-md border border-k-gray/40">
        {services.map(({ option, dateLabel }, index) => {
          const detail = extraExperiences?.[option?.optionId ?? option?.id];
          const included = detail?.includedServices;

          return (
            <div
              key={`${option?.optionId ?? option?.id}-${index}`}
              className={[
                'px-5 py-4',
                index % 2 === 1 ? 'border-l border-k-gray/30' : '',
                index >= 2 ? 'border-t border-k-gray/30' : '',
              ].join(' ')}
              data-prevent-cut
            >
              {dateLabel && (
                <div className="mb-1 text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
                  {dateLabel}
                </div>
              )}
              <div
                className="text-lg leading-tight"
                style={{ fontFamily: 'var(--td-font-display)' }}
              >
                {detail?.name ?? option?.name}
              </div>
              {included && (
                <div className="mt-2">
                  <div className="text-3xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
                    {includedLabel}
                  </div>
                  <div
                    className="export-rich-text text-2xs leading-snug text-[var(--td-ink-soft)]"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeDiaryHtml(included),
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InclusionColumn({ label, html, emptyLabel, variant }) {
  return (
    <div>
      <div className="mb-3 border-b border-k-gray/30 pb-2 text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
        {label}
      </div>
      {html ? (
        <div
          className={`export-rich-text export-inclusion export-inclusion--${variant} text-sm leading-relaxed`}
          dangerouslySetInnerHTML={{ __html: sanitizeDiaryHtml(html) }}
        />
      ) : (
        <div className="text-xs italic text-[var(--td-ink-muted)]">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

export default function ExportPrograms({
  trip,
  tourOperator,
  settings,
  group,
  index = 0,
  total = 1,
  extraExperiences,
  wholeTripServices = [],
}) {
  const sectionLabel = getSettingLabel(settings, 'programs');
  const programsSection = getSetting(settings, 'programs');
  const title = getSettingLabel(programsSection?.sections, 'programs-title');
  const emptyLabel = getSettingLabel(
    programsSection?.sections,
    'programs-empty',
  );
  const servicesLabel = getSettingLabel(
    programsSection?.sections,
    'programs-services',
  );

  const inclusionsSection = getSetting(settings, 'included-excluded-services');
  const includedLabel = getSettingLabel(
    inclusionsSection?.sections,
    'included-service',
  );
  const excludedLabel = getSettingLabel(
    inclusionsSection?.sections,
    'excluded-service',
  );

  const { experience, dayIndices } = group;
  const first = dayIndices[0];
  const last = dayIndices[dayIndices.length - 1];
  const dayLabel =
    getSettingLabel(
      getSetting(getSetting(settings, 'days')?.sections, 'program-of-the-day')
        ?.fields,
      'pod-day-number',
    ) || 'Day';
  const daysLabel =
    getSettingLabel(
      getSetting(settings, 'cover-page')?.sections,
      'cover-page-days',
    ) || 'Days';
  const dayRange =
    first === last
      ? `${dayLabel} ${first + 1}`
      : `${daysLabel} ${first + 1}–${last + 1}`;
  const dateRange = formatRange(dayDate(trip, first), dayDate(trip, last));

  const services = [
    ...collectServices(trip, dayIndices),
    ...wholeTripServices.map((service) => ({
      ...service,
      dateLabel: globalServiceDateLabel(trip, service.option, extraExperiences),
    })),
  ];

  return (
    <ExportFlowPage
      sectionLabel={sectionLabel}
      title={title}
      badge={total > 1 ? `${index + 1} / ${total}` : null}
      trip={trip}
      tourOperator={tourOperator}
      settings={settings}
      footerStatus
    >
      <article
        className="mt-4 rounded-md border border-k-gray/40"
        data-prevent-cut
      >
        <div className="border-b border-k-gray/30 bg-[var(--td-paper-secondary)] px-5 py-4">
          <div className="text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
            {dayRange}
            {dateRange && <span className="opacity-60"> · {dateRange}</span>}
          </div>
          <div
            className="mt-1 text-xl leading-tight"
            style={{ fontFamily: 'var(--td-font-display)' }}
          >
            {experience?.name}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 px-5 py-5">
          <InclusionColumn
            label={includedLabel}
            html={experience?.includedServices}
            emptyLabel={emptyLabel}
            variant="included"
          />
          <InclusionColumn
            label={excludedLabel}
            html={experience?.excludedServices}
            emptyLabel={emptyLabel}
            variant="excluded"
          />
        </div>
      </article>

      <ServiceList
        title={servicesLabel}
        services={services}
        extraExperiences={extraExperiences}
        includedLabel={includedLabel}
      />
    </ExportFlowPage>
  );
}
